// @flow
import React, { Fragment } from 'react';
import DealerModel from 'Stores/DealerModel';
import LocationCard from '../LocationCard';
import classNames from 'classnames/bind';
import Carousel from 'Components/Carousel';
import Button from 'Components/Button';
import styles from './styles.scss';
import { useDebounce } from 'Utils/useDebounce';
import { v4 as uuidv4 } from 'uuid';

const cx = classNames.bind(styles);

declare var PUBLIC_BASE_URL: string;

type Props = {
  id?: string,
  dealer: DealerModel,
  open: boolean,
  init: boolean,
  toggle?: (number) => void,
  showMapClicked: boolean,
  setShowMapClicked: () => void,
};

type State = {
  branches: Array<Object>,
  currentActive: boolean,
  selectedItem: number,
  currentBranch: Object,
  cardsInView: number,
  openedHoursAccordions: Array<String>,
  isTransitioning: boolean,
};

export default class LocationSwitch extends React.Component<Props, State> {
  static defaultProps = {
    id: `location-switch-${uuidv4()}`,
  };

  constructor(props: Props) {
    super(props);

    // Move current branch to first position
    const sortedBranches = props.dealer.branches.sort(function (a, b) {
      if (a.mazdaId === props.dealer.id) {
        return -1;
      }
      return 1;
    });

    this.carouselRef = React.createRef();

    this.state = {
      branches: sortedBranches,
      currentActive: true,
      selectedItem: 0,
      currentBranch: sortedBranches[0],
      cardsInView: 1,
      openedHoursAccordions: [],
      isTransitioning: false,
    };
  }

  componentDidMount() {
    this.setDimensions();
    window.addEventListener('resize', useDebounce({ func: this.setDimensions }));
  }

  componentDidUpdate(prevProps) {
    if (prevProps.forceOneCard !== this.props.forceOneCard) {
      this.setDimensions();
    }

    if (prevProps.open !== this.props.open) {
      this.setState({ isTransitioning: true });
    }
  }

  onChange = (position: number) => {
    const { branches } = this.state;

    this.setState({ selectedItem: position });
    this.setState({ currentBranch: branches[position] });

    if (position === 0) {
      this.setState({ currentActive: true });
    } else {
      this.setState({ currentActive: false });
    }
  };

  next = () => {
    this.carouselRef.current.next();
  };

  prev = () => {
    this.carouselRef.current.prev();
  };

  setDimensions = () => {
    const currentWidth = window.innerWidth;
    const { forceOneCard } = this.props;

    if (forceOneCard) {
      this.setState({ cardsInView: 1 });
    } else {
      if (currentWidth < 1000) this.setState({ cardsInView: 1 });
      if (currentWidth > 1000 && currentWidth < 1500) this.setState({ cardsInView: 2 });
      if (currentWidth > 1500 && currentWidth < 2000) this.setState({ cardsInView: 3 });
      if (currentWidth > 2000) this.setState({ cardsInView: 4 });
    }
  };

  resetOpenedHoursAccordions = () => {
    this.setState({
      openedHoursAccordions: [],
    });
  };

  outerControlToggle = (accordionName) => {
    const { openedHoursAccordions } = this.state;

    if (openedHoursAccordions.includes(accordionName)) {
      this.setState({
        openedHoursAccordions: [...openedHoursAccordions.filter((item) => item !== accordionName)],
      });
    } else {
      const newOpenedHoursAccordions = [...openedHoursAccordions].filter(
        (item) => !item.includes(accordionName.substr(0, 5))
      );

      this.setState({
        openedHoursAccordions: [...newOpenedHoursAccordions, accordionName],
      });
    }
  };

  onTransitionEnd = (event) => {
    const element = document.querySelector(`#${this.props.id}`);

    if (event.target === element) {
      this.setState({ isTransitioning: false });
    }
  };

  renderLocationCards = () => {
    const { dealer, setSharedState } = this.props;
    const { branches, cardsInView, openedHoursAccordions } = this.state;

    const cards = branches.map((branch, i) => (
      <LocationCard
        e2e={`location_${i}`}
        key={`card-${i}-${branch.mazdaId}`}
        image={dealer.settings.aboutus.image}
        dealer={dealer}
        branch={branch}
        isCurrentBranch={branch.mazdaId === dealer.id}
        outerControlToggle={this.outerControlToggle}
        openedHoursAccordions={openedHoursAccordions}
        setSharedState={setSharedState}
        tabindex={0}
      />
    ));

    const stages = [];
    const stageCount = Math.ceil(cards.length / cardsInView);
    for (let stage = 0; stage < stageCount; stage++) {
      stages[stage] = [];
      for (let card = stage * cardsInView; card < cardsInView * (stage + 1); card++) {
        if (cards[card]) {
          stages[stage].push(cards[card]);
        }
      }
    }

    return stages.map((stage, cardIndex) => {
      const slideHasCurrentSelectedLocation =
        this.state.selectedItem >= cardIndex &&
        this.state.selectedItem <= cardIndex + stages.length - 1;
      const ariaLabelCurrentSelectedLocation = slideHasCurrentSelectedLocation
        ? 'aktuelle Standort-Kachel - '
        : '';
      const ariaLabelSlide = `${cardIndex + 1}. von ${stages.length} Slide${stages.length !== 1 ? 's' : ''}`;
      const ariaLabelCard = `mit ${stage.length} Kachel${stage.length !== 1 ? 'n' : ''}`;

      return (
        <Fragment key={`view-${cardIndex}`}>
          <div
            className={styles.locationCardContainer}
            role="group"
            aria-roledescription="Slide"
            aria-label={`${ariaLabelCurrentSelectedLocation}${ariaLabelSlide} ${ariaLabelCard}`}
          >
            {stage}
          </div>
        </Fragment>
      );
    });
  };

  render() {
    const { id, dealer, open, toggle } = this.props;
    const { isTransitioning } = this.state;
    const views = this.renderLocationCards();
    const toggleButtonId = `toogle-button-${uuidv4()}`;
    const hasMultipleBranches = dealer.branches && dealer.branches.length > 1;
    const toggleButtonLabel = `Standort${hasMultipleBranches ? 'e' : ''} ${open ? 'verbergen' : 'anzeigen'}`;
    const attributeHidden = !open && !isTransitioning;

    return (
      <div
        className={cx('header__location-switch', open && 'header__location-switch--open')}
        id={id}
        style={{
          backgroundImage: `url(${PUBLIC_BASE_URL}/img/map-placeholder.png)`,
        }}
        role="region"
        aria-labelledby={toggleButtonId}
        aria-hidden={attributeHidden}
        hidden={attributeHidden}
        tabIndex={-1}
        onTransitionEnd={this.onTransitionEnd}
      >
        <Carousel
          showArrows
          highlightedArrows
          showStatus={false}
          transparentBackground
          transitionTime={700}
          onChange={this.resetOpenedHoursAccordions}
        >
          {views}
        </Carousel>
        <Button
          id={toggleButtonId}
          className={styles.closeButton}
          variant="primary"
          icon="close"
          onClick={toggle}
          ariaExpanded={open === true}
          ariaControls={id}
          ariaLabel={toggleButtonLabel}
          e2e="closeourlocations"
        />
      </div>
    );
  }
}
